// Usar ruta relativa para aprovechar el proxy de Vue
const API_BASE = '/api'

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value)
    }
  })
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

export const apiService = {
  // Equipos
  async getTeams(filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(`${API_BASE}/teams${query}`)
    return await response.json()
  },

  async getTeam(teamId) {
    const response = await fetch(`${API_BASE}/teams/${teamId}`)
    return await response.json()
  },

  async getTeamByName(teamName, league, season) {
    const params = buildQueryString({
      league,
      season: season || undefined
    })
    const response = await fetch(`${API_BASE}/teams/by-name/${encodeURIComponent(teamName)}${params}`)
    return await response.json()
  },

  // Clasificaciones
  async getLeagueTables(season) {
    const query = buildQueryString(season ? { season } : {})
    const response = await fetch(`${API_BASE}/league-tables${query}`)
    return await response.json()
  },

  async getLeagueTable(league, season) {
    const query = buildQueryString(season ? { season } : {})
    const response = await fetch(`${API_BASE}/league-tables/${encodeURIComponent(league)}${query}`)
    return await response.json()
  },

  async getTeamPosition(league, team, season) {
    const query = buildQueryString(season ? { season } : {})
    const response = await fetch(`${API_BASE}/league-tables/${encodeURIComponent(league)}/${encodeURIComponent(team)}${query}`)
    return await response.json()
  },

  // Jugadores
  async getPlayers(filters = {}) {
    const params = new URLSearchParams(filters)
    const response = await fetch(`${API_BASE}/players?${params}`)
    return await response.json()
  },

  async getTeamPlayers(teamName, league) {
    const params = league ? `?league=${encodeURIComponent(league)}` : ''
    const response = await fetch(`${API_BASE}/players/team/${encodeURIComponent(teamName)}${params}`)
    return await response.json()
  },

  async getTopScorers(league, limit = 10) {
    const response = await fetch(`${API_BASE}/players/top-scorers/${encodeURIComponent(league)}?limit=${limit}`)
    return await response.json()
  },

  // Partidos y Calendario
  async getTeamFixtures(teamName) {
    const response = await fetch(`${API_BASE}/players/fixtures/team/${encodeURIComponent(teamName)}`)
    return await response.json()
  },

  async getUpcomingMatches(league, limit = 20) {
    const params = new URLSearchParams({ league, limit })
    const response = await fetch(`${API_BASE}/players/fixtures/upcoming?${params}`)
    return await response.json()
  },

  async getHeadToHead(team1, team2) {
    const response = await fetch(`${API_BASE}/players/fixtures/head-to-head/${encodeURIComponent(team1)}/${encodeURIComponent(team2)}`)
    return await response.json()
  },

  // Partidos originales
  async getMatches({ status, season, league, limit = 50, page = 1 } = {}) {
    const query = buildQueryString({
      status,
      season,
      league,
      limit,
      page
    })
    const response = await fetch(`${API_BASE}/matches${query}`)
    return await response.json()
  },

  // Actualización de datos
  async updateStats(league = 'all') {
    const response = await fetch(`${API_BASE}/update/update-stats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ league })
    })
    return await response.json()
  },

  async getUpdateStatus() {
    const response = await fetch(`${API_BASE}/update/update-status`)
    return await response.json()
  },

  // Actualización híbrida (API-Football + FBref)
  async hybridUpdate(league, includeAdvancedStats = false) {
    const response = await fetch(`${API_BASE}/hybrid-update/hybrid-update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        league,
        includeAdvancedStats 
      })
    })
    return await response.json()
  },

  async getProvidersInfo() {
    const response = await fetch(`${API_BASE}/hybrid-update/providers-info`)
    return await response.json()
  },

  async getLeagueAverages({ league, season }) {
    const query = buildQueryString({ league, season });
    const response = await fetch(`${API_BASE}/teams/averages${query}`);
    return await response.json();
  },

  // Fixtures y calendario mejorado
  async getFixtures(league = 'La Liga', options = {}) {
    const { season, ...rest } = options
    const params = new URLSearchParams({ league, ...rest })
    if (season) {
      params.append('season', season)
    }
    const response = await fetch(`${API_BASE}/matches?${params}`)
    return await response.json()
  },

  async getUefaFixtures({ leagueId, season, round, from, to, status } = {}) {
    const query = buildQueryString({
      leagueId,
      season,
      round,
      from,
      to,
      status
    })
    const response = await fetch(`${API_BASE}/uefa/fixtures${query}`)
    return await response.json()
  },

  async getUefaFixtureExportMeta(fixtureIds = []) {
    const response = await fetch(`${API_BASE}/uefa/fixtures/export-meta/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fixtureIds })
    })
    return await response.json()
  },

  async upsertUefaFixtureExportMeta({ fixtureId, data } = {}) {
    const response = await fetch(`${API_BASE}/uefa/fixtures/export-meta/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fixtureId, data })
    })
    return await response.json()
  },

  async getUefaStandings({ leagueId, season } = {}) {
    const query = buildQueryString({
      leagueId,
      season
    })
    const response = await fetch(`${API_BASE}/uefa/standings${query}`)
    return await response.json()
  },

  async getUefaRounds({ leagueId, season } = {}) {
    const query = buildQueryString({
      leagueId,
      season
    })
    const response = await fetch(`${API_BASE}/uefa/rounds${query}`)
    return await response.json()
  },

  async recalculateUefaSeason({
    season,
    leagueIds,
    simulatedScores,
    category,
    applyPolicyOverrides,
    applyLiveBonuses,
    applyLiveQualifyingBonuses,
    bonusMode,
    dryRun,
    asOfUtc,
    includeSnapshots,
    publishOfficial
  } = {}) {
    const response = await fetch(`${API_BASE}/uefa/recalculate/season`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        leagueIds,
        simulatedScores,
        category,
        applyPolicyOverrides,
        applyLiveBonuses,
        applyLiveQualifyingBonuses,
        bonusMode,
        dryRun,
        asOfUtc,
        includeSnapshots,
        publishOfficial
      })
    })
    return await response.json()
  },

  async auditUefaScoring({
    seasonLabel,
    category,
    competitionCode,
    roundText,
    status,
    homeGoals,
    awayGoals,
    forceTwoLeg
  } = {}) {
    const response = await fetch(`${API_BASE}/uefa/scoring/audit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        seasonLabel,
        category,
        competitionCode,
        roundText,
        status,
        homeGoals,
        awayGoals,
        forceTwoLeg
      })
    })
    return await response.json()
  },

  async getUefaMissingTeams({ season, leagueIds, category } = {}) {
    const response = await fetch(`${API_BASE}/uefa/missing-teams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        leagueIds,
        category
      })
    })
    return await response.json()
  },

  async importUefaClubMaster({ countryCodeMap, resetClubs = false } = {}) {
    const response = await fetch(`${API_BASE}/uefa/import/kassiesa/club-master`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        countryCodeMap: countryCodeMap || {},
        resetClubs
      })
    })
    return await response.json()
  },

  async getUefaAssociationRankings(season, category = 'men') {
    const query = buildQueryString({ season })
    const base = category === 'women' ? '/uefa/women/rankings' : '/uefa/rankings'
    const response = await fetch(`${API_BASE}${base}${query}`)
    return await response.json()
  },

  async getUefaAssociationSeasons(season, category = 'men') {
    const query = buildQueryString({ season })
    const base = category === 'women' ? '/uefa/women/associations/seasons' : '/uefa/associations/seasons'
    const response = await fetch(`${API_BASE}${base}${query}`)
    return await response.json()
  },

  async getUefaPointsEvolution({
    season,
    category = 'men',
    associationCodes = [],
    clubs = [],
    includeForecast = false
  } = {}) {
    const response = await fetch(`${API_BASE}/uefa/points-evolution`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        category,
        associationCodes,
        clubs,
        includeForecast
      })
    })
    return await response.json()
  },

  async queryUefaAssistant({
    prompt,
    season,
    category = 'men',
    context = null
  } = {}) {
    const response = await fetch(`${API_BASE}/uefa/assistant/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt,
        season,
        category,
        context
      })
    })
    return await response.json()
  },

  async getUefaClubs(season, category = 'men') {
    const query = buildQueryString({ season })
    const base = category === 'women' ? '/uefa/women/clubs' : '/uefa/clubs'
    const response = await fetch(`${API_BASE}${base}${query}`)
    return await response.json()
  },
  async getUefaClubRankings(season, windowYears = 5, category = 'men') {
    const query = buildQueryString({ season, windowYears })
    const base = category === 'women' ? '/uefa/women/clubs/ranking' : '/uefa/clubs/ranking'
    const response = await fetch(`${API_BASE}${base}${query}`)
    return await response.json()
  },
  async getUefaClubRankingsPersisted(season, windowYears = 5, category = 'men') {
    const query = buildQueryString({ season, windowYears })
    const base = category === 'women'
      ? '/uefa/women/clubs/ranking/persisted'
      : '/uefa/clubs/ranking/persisted'
    const response = await fetch(`${API_BASE}${base}${query}`)
    return await response.json()
  },

  async getSeasonConfigs(filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(`${API_BASE}/uefa/master/seasons${query}`)
    return await response.json()
  },

  async createSeasonConfig(payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async cloneSeasonConfig(payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/clone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async prepareNextSeason(payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/prepare-next`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async updateSeasonConfig(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonConfig(seasonLabel) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}`, {
      method: 'DELETE'
    })
    return await response.json()
  },

  async seedSeasonConfig(seasonLabel) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/seed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    return await response.json()
  },

  async bootstrapHistoricalTemplate(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/bootstrap-historical-template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async importSeasonParticipants(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/participants/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async createMissingParticipantClubs(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/participants/create-missing-clubs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async getSeasonConfigDetail(seasonLabel, filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/config${query}`
    )
    return await response.json()
  },

  async recalculateHistoricalSeasonResults(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/results/recalculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async analyzeHistoricalSeasonResults(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/results/diagnostics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async mapHistoricalUnmatchedClub(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/results/map-unmatched-club`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async saveSeasonClubEntry(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/club-entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonClubEntry(seasonLabel, id, filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/club-entries/${encodeURIComponent(id)}${query}`, {
      method: 'DELETE'
    })
    return await response.json()
  },

  async bulkDeleteSeasonClubEntries(seasonLabel, rows = []) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/club-entries/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows })
    })
    return await response.json()
  },

  async saveSeasonAssociationEntry(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/association-entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async preloadSeasonEntries(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/preload-entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonAssociationEntry(seasonLabel, id, filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/association-entries/${encodeURIComponent(id)}${query}`, {
      method: 'DELETE'
    })
    return await response.json()
  },

  async bulkDeleteSeasonAssociationEntries(seasonLabel, rows = []) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/association-entries/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows })
    })
    return await response.json()
  },

  async saveSeasonCompetition(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/competitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonCompetition(seasonLabel, competitionCode) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/competitions/${encodeURIComponent(competitionCode)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async saveSeasonPhase(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/phases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonPhase(seasonLabel, competitionCode, phaseCode) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/phases/${encodeURIComponent(competitionCode)}/${encodeURIComponent(phaseCode)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async saveSeasonRoundOverride(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/round-overrides`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonRoundOverride(seasonLabel, id) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/round-overrides/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async saveSeasonScoringRule(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/scoring-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonScoringRule(seasonLabel, id) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/scoring-rules/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async saveSeasonBonusRule(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/bonus-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonBonusRule(seasonLabel, id) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/bonus-rules/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async saveSeasonTransition(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/transitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonTransition(seasonLabel, id) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/transitions/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async getSeasonImports(seasonLabel, filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/imports${query}`
    )
    return await response.json()
  },

  async saveSeasonImport(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/imports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonImport(seasonLabel, id) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/imports/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async getSeasonFixtures(seasonLabel, filters = {}) {
    const query = buildQueryString(filters)
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures${query}`
    )
    return await response.json()
  },

  async saveSeasonFixture(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async importSeasonFixtures(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async importSeasonFixturesFromApi(seasonLabel, payload = {}) {
    const response = await fetch(`${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures/import-api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  getSeasonFixtureTemplateUrl(seasonLabel, format = 'csv') {
    return `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures/template.${format}`
  },

  async getSeasonFixtureImportSpec(seasonLabel) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures/import-spec`
    )
    return await response.json()
  },

  async getApiDataMetadata() {
    const response = await fetch(`${API_BASE}/uefa/api-data/metadata`)
    return await response.json()
  },

  async executeApiDataRequest(payload) {
    const response = await fetch(`${API_BASE}/uefa/api-data/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async getApiDataLogs() {
    const response = await fetch(`${API_BASE}/uefa/api-data/logs`)
    return await response.json()
  },

  async clearApiDataLogs() {
    const response = await fetch(`${API_BASE}/uefa/api-data/logs`, {
      method: 'DELETE'
    })
    return await response.json()
  },

  async getApiDataStaging() {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging`)
    return await response.json()
  },

  async saveApiDataStaging(payload) {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async clearApiDataStaging() {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging`, {
      method: 'DELETE'
    })
    return await response.json()
  },

  async deleteApiDataStaging(id) {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    })
    return await response.json()
  },

  async importApiDataStagingToSeasonFixtures(id, payload) {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging/${encodeURIComponent(id)}/import-fixtures`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async compareApiDataStagingAgainstEntries(id, payload) {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging/${encodeURIComponent(id)}/compare-entries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async addApiDataStagingCandidatesToEntries(id, payload) {
    const response = await fetch(`${API_BASE}/uefa/api-data/staging/${encodeURIComponent(id)}/add-entry-candidates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    return await response.json()
  },

  async deleteSeasonFixture(seasonLabel, id) {
    const response = await fetch(
      `${API_BASE}/uefa/master/seasons/${encodeURIComponent(seasonLabel)}/fixtures/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    return await response.json()
  },

  async createUefaRankingSnapshot({ season, category = 'men' } = {}) {
    const response = await fetch(`${API_BASE}/uefa/rankings/snapshot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ season, category })
    })
    return await response.json()
  },

  async getUefaRankingSnapshotDiff({ season, category = 'men' } = {}) {
    const query = buildQueryString({ season, category })
    const response = await fetch(`${API_BASE}/uefa/rankings/snapshot/diff${query}`)
    return await response.json()
  },

  async getKassiesaLatestDiff({ season, source = 'kassiesa', details = false } = {}) {
    const query = buildQueryString({ season, source, details: details ? 1 : undefined })
    const response = await fetch(`${API_BASE}/uefa/diff/kassiesa/latest${query}`)
    return await response.json()
  },

  async diffKassiesaFromHtml({ season, html, countryCodeMap = {}, saveSnapshot = false } = {}) {
    const response = await fetch(`${API_BASE}/uefa/diff/kassiesa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        html,
        countryCodeMap,
        saveSnapshot
      })
    })
    return await response.json()
  },

  async diffUefaClubRanking({ season, category = 'men' } = {}) {
    const response = await fetch(`${API_BASE}/uefa/diff/uefa/club-ranking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ season, category })
    })
    return await response.json()
  },

  async diffUefaRankingManual({ season, category = 'men', countryHtml, clubHtml } = {}) {
    const response = await fetch(`${API_BASE}/uefa/diff/uefa/ranking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        category,
        countryHtml,
        clubHtml
      })
    })
    return await response.json()
  },

  async diffKassiesaRanking({ season, category = 'men', countryHtml, clubHtml, privateKassiesa = false, bonusMode, applyPolicyOverrides } = {}) {
    const response = await fetch(`${API_BASE}/uefa/diff/kassiesa/ranking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        category,
        countryHtml,
        clubHtml,
        privateKassiesa,
        bonusMode,
        applyPolicyOverrides
      })
    })
    return await response.json()
  },

  async diffKassiesaRankingLive({ season, category = 'men', crankUrl, trankUrl, privateKassiesa = false, bonusMode, applyPolicyOverrides } = {}) {
    const response = await fetch(`${API_BASE}/uefa/diff/kassiesa/ranking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        season,
        category,
        liveFetch: true,
        crankUrl,
        trankUrl,
        privateKassiesa,
        bonusMode,
        applyPolicyOverrides
      })
    })
    return await response.json()
  },

  async getKassiesaHealth({ season, crankUrl, trankUrl } = {}) {
    const query = buildQueryString({ season, crankUrl, trankUrl })
    const response = await fetch(`${API_BASE}/uefa/kassiesa/health${query}`)
    return await response.json()
  },

  async getMatchById(matchId) {
    const response = await fetch(`${API_BASE}/matches/${matchId}`)
    return await response.json()
  },

  // Método genérico GET para compatibilidad
  async get(endpoint) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`
    const response = await fetch(url)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw {
        response: {
          data: errorData,
          status: response.status,
          statusText: response.statusText
        }
      }
    }
    
    return await response.json()
  },

  // Métodos genéricos para compatibilidad con axios-like API
  async get(endpoint) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`
    const response = await fetch(url)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw {
        response: {
          data: errorData,
          status: response.status,
          statusText: response.statusText
        }
      }
    }
    
    const data = await response.json()
    return { data } // Devolver en formato axios { data: ... }
  },

  async post(endpoint, body = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw {
        response: {
          data: errorData,
          status: response.status,
          statusText: response.statusText
        }
      }
    }
    
    const data = await response.json()
    return { data } // Devolver en formato axios { data: ... }
  }
}

export default apiService
